/**
 * FROZEN CONTRACT — the quest/story layer's data shapes (owner decision D17).
 *
 * Authored quest DEFINITIONS live in data/quests/ (read-only at runtime); the
 * save stores per-quest PROGRESS only (QuestState), keyed by def ids — so
 * authored content can be edited/extended without a migration. The pure rules
 * engine lives in core/quests.ts; GameSession owns persistence + reward grants.
 *
 * Every design choice here obeys the no-pressure contract (CLAUDE.md + D17):
 * quests never fail, never expire, never gate the cozy loop; availability is a
 * soft VISIBILITY gate only; objectives auto-track off real play events.
 *
 * Spec: notes/story-design/quest-system.md (§2–§5).
 */

/** Main-story spine vs. optional flavor. Both are pressure-free. */
export type QuestKind = 'main' | 'side';

/** Who offers/turns in the quest: an existing regular's SimTrainer id (e.g.
 *  't7') or a NEW authored story-NPC id (e.g. 'npc-leader-meadow'). */
export type QuestGiverId = string;

export interface StageDialogue {
  /** Shown when the stage becomes active (the "here's what I'd love" nudge). */
  readonly onStart: readonly string[];
  /** Shown when the stage's objectives complete (the warm "you did it!"). */
  readonly onComplete: readonly string[];
}

/** How/whether a quest is surfaced. All conditions are soft gates on
 *  VISIBILITY only — they never block progress elsewhere in the game. */
export interface QuestAvailability {
  /** e.g. 'always' | 'badge' | 'quest-done' | 'friendship' | 'caught-count'. */
  readonly kind: string;
  readonly ref?: string; // e.g. 'main-badge-2', 'water-badge'
  readonly threshold?: number;
}

/** Typed objectives, auto-tracked off REAL session events (never a menu chore). */
export type ObjectiveDef =
  /** Catch a SPECIFIC species (dataset slug, e.g. 'growlithe'). */
  | { readonly kind: 'catch-species'; readonly speciesId: string }
  /** Catch N of a TYPE (e.g. 'bug', 'water') — species type from the dataset. */
  | { readonly kind: 'catch-type'; readonly monType: string; readonly count: number }
  /** Catch any N wild Pokémon. */
  | { readonly kind: 'catch-count'; readonly count: number }
  /** Win N friendly/gym battles (a loss just doesn't tick — never punishes). */
  | { readonly kind: 'win-friendly-battles'; readonly count: number }
  /** Beat a specific gym leader (see main-quest.md). */
  | { readonly kind: 'defeat-gym-leader'; readonly gymId: string }
  /** Get ANY team member (or a specific species) to a level. */
  | { readonly kind: 'reach-level'; readonly level: number; readonly speciesId?: string }
  /** Set foot in a biome (by biomeId, e.g. 'still-lake'). */
  | { readonly kind: 'visit-biome'; readonly biomeId: string }
  /** Evolve N times (or into a specific target species, e.g. 'ivysaur'). */
  | { readonly kind: 'evolve'; readonly count: number; readonly toSpeciesId?: string }
  /** Complete N trades (or a trade with a specific trainer). */
  | { readonly kind: 'trade'; readonly count: number; readonly withTrainerId?: string }
  /** Show off a team Pokémon in chat N times (D14 peacocking). */
  | { readonly kind: 'show-off'; readonly count: number; readonly speciesId?: string }
  /** Cast a move in the tavern — any N, a specific move, or a move of a type. */
  | {
      readonly kind: 'cast-move';
      readonly count: number;
      readonly moveId?: string;
      readonly monType?: string;
    }
  /** Say hi to a specific regular (greet in Town). */
  | { readonly kind: 'talk-to'; readonly trainerId: string };

export interface QuestStage {
  readonly id: string; // 'stage-1'
  /** ALL objectives in a stage must be met to advance (implicit AND). */
  readonly objectives: readonly ObjectiveDef[];
  /** Authored FLOOR lines; the voice layer phrases beats around them. */
  readonly dialogue: StageDialogue;
}

/** Rewards the session grants DIRECTLY into the world (no money/inventory —
 *  the game has neither). None is consumable/loseable; none gates progress. */
export type QuestReward =
  /** A gifted Pokémon, built faithfully + added via team-if-room-else-box + dex. */
  | {
      readonly kind: 'gift-mon';
      readonly speciesId: string;
      readonly level: number;
      readonly nickname?: string;
    }
  /** Unlock a codex entry immediately (monotonic, never removed). */
  | { readonly kind: 'codex-unlock'; readonly entryId: string }
  /** Award a gym badge (append to QuestState.badges; the Badge Case renders it). */
  | { readonly kind: 'badge'; readonly badgeId: string }
  /** Apply N Rare Candies to a chosen mon (reuses core/items.applyRareCandy). */
  | { readonly kind: 'rare-candies'; readonly count: number }
  /** Grant a specific evolution stone USE on a chosen mon. */
  | { readonly kind: 'evolution-stone'; readonly itemId: string }
  /** A cosmetic town-growth prop flag (pure decoration, read from completed ids). */
  | { readonly kind: 'town-prop'; readonly propId: string }
  /** A follower cosmetic / the right to set an authored special follower. */
  | { readonly kind: 'follower-cosmetic'; readonly cosmeticId: string };

/** An authored quest definition (DATA — data/quests/, never in the save). */
export interface QuestDef {
  readonly id: string; // stable, e.g. 'main-badge-1', 'side-breeder-eggs'
  readonly kind: QuestKind;
  readonly title: string; // "The Meadow Badge"
  readonly giver: QuestGiverId;
  readonly synopsis: string; // one warm line shown in the log
  /** Ordered stages. A quest completes when the last stage's objectives are met. */
  readonly stages: readonly QuestStage[];
  /** Optional: only OFFERED once satisfied (visibility only, never a wall). */
  readonly available?: QuestAvailability;
  /** Reward granted on final turn-in. */
  readonly reward: QuestReward;
  /** Beat-flavor tags the voice layer leans on. */
  readonly tone?: readonly string[]; // ['warm','proud','mysterious']
}

// ── Progress (what the SAVE stores) ─────────────────────────────────────────────

export interface ObjectiveProgress {
  /** Index of the objective within its stage (stable positional key). */
  readonly index: number;
  /** Current tally toward the count (0 for a not-yet-met boolean objective). */
  count: number;
  done: boolean;
}

export interface QuestProgress {
  readonly questId: string;
  /** Index of the active stage; === stages.length once fully complete. */
  stageIndex: number;
  /** Progress for the ACTIVE stage's objectives (rebuilt on stage advance). */
  objectives: readonly ObjectiveProgress[];
  readonly startedAt: number; // wall-clock ms; "you've been at this" flavor only
  turnedIn: boolean; // true once the reward is granted
}

export interface QuestState {
  /** Quests the player has started (offered + accepted). Keyed by questId. */
  readonly active: readonly QuestProgress[];
  /** Ids of fully turned-in quests (availability gates + the log's Done tab). */
  readonly completed: readonly string[];
  /** Earned gym badge ids, in earn order (the Badge Case reads this). */
  readonly badges: readonly string[];
}

/** A fresh, empty quest block (new saves + the v5→v6 migration back-fill). */
export function emptyQuestState(): QuestState {
  return { active: [], completed: [], badges: [] };
}

// ── Events (what the session emits; the pure engine folds them in) ──────────────

export type QuestEvent =
  | { kind: 'catch'; speciesId: string; monType: string }
  | { kind: 'battle-win'; battleKind: 'wild' | 'friendly' | 'gym'; gymId?: string }
  | { kind: 'level'; speciesId: string; level: number }
  | { kind: 'visit-biome'; biomeId: string }
  | { kind: 'evolve'; toSpeciesId: string }
  | { kind: 'trade'; withTrainerId: string }
  | { kind: 'show-off'; speciesId: string }
  | { kind: 'cast-move'; moveId: string; monType: string }
  | { kind: 'talk-to'; trainerId: string };

/** The result of folding one event into quest progress (core/quests.advanceQuests). */
export interface QuestAdvance {
  readonly next: QuestState; // new immutable state
  readonly stageCleared: readonly { questId: string; stageIndex: number }[];
  readonly completed: readonly string[]; // quests that just finished (ready to turn in)
}
